import React, { useEffect, useMemo, useState } from "react";
import api, { extractError } from "@/lib/api";
import { CLASSIFICATIONS } from "@/lib/constants";
import { fmtDateTime } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DictationTextarea } from "@/components/DictationTextarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, Save, User, MapPin, Phone, Link2, Tags, Info } from "lucide-react";
import { toast } from "sonner";

const GENERI = ["Femminile", "Maschile", "Altro", "Non specificato"];
const MODALITA = ["Telefono", "WhatsApp", "Email", "SMS"];
const FASCE = ["Mattina", "Pomeriggio", "Sera"];
const STATI_RELAZIONE = ["Attivo", "In valutazione", "Sospeso", "Concluso", "Archiviato"];

const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

// Calcolo età lato client (indicativo; il backend ricalcola al salvataggio)
const calcEta = (dataNascita) => {
  if (!dataNascita) return "";
  try {
    const d = new Date(dataNascita);
    const t = new Date();
    let e = t.getFullYear() - d.getFullYear();
    if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) e -= 1;
    return e >= 0 && e < 130 ? e : "";
  } catch { return ""; }
};

const FIELDS = [
  "nome", "cognome", "ragione_sociale", "ente_committente", "codice_fiscale", "partita_iva",
  "data_nascita", "luogo_nascita", "genere", "indirizzo", "numero_civico", "cap", "citta",
  "provincia", "telefono_principale", "telefono_secondario", "whatsapp", "email", "pec",
  "modalita_contatto", "fascia_oraria", "referente_familiare", "note", "origine_contatto",
  "stato_relazione", "numero_tessera", "codice_ente_affiliazione",
];

const buildInitial = (p) => {
  const f = {};
  FIELDS.forEach((k) => { f[k] = p?.[k] || ""; });
  f.data_nascita = (p?.data_nascita || "").slice(0, 10);
  f.tags = (p?.tags || []).join(", ");
  f.classificazioni = p?.classificazioni || [];
  f.enti_ids = p?.enti_ids || [];
  f.servizi_ids = p?.servizi_ids || [];
  return f;
};

export const AggiornaAnagraficaDialog = ({ open, setOpen, person, onDone }) => {
  const [f, setF] = useState(buildInitial(person));
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [entities, setEntities] = useState([]);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    if (!open) return;
    setF(buildInitial(person));
    api.get("/services").then(({ data }) => setServices(data)).catch((e) => console.error("Errore servizi", e));
    api.get("/entities").then(({ data }) => setEntities(data)).catch((e) => console.error("Errore enti", e));
    api.get("/team").then(({ data }) => setTeam(data)).catch((e) => console.error("Errore team", e));
  }, [open, person]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleInArray = (k, value) => setF((s) => {
    const arr = s[k] || [];
    return { ...s, [k]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
  });

  const eta = useMemo(() => calcEta(f.data_nascita), [f.data_nascita]);
  const creatorName = useMemo(() => {
    if (!person?.created_by) return "-";
    const u = team.find((x) => x.id === person.created_by);
    return u?.nome_completo || person.created_by;
  }, [team, person]);

  const save = async () => {
    if (!f.cognome && !f.ragione_sociale) {
      toast.error("Inserisci almeno il cognome o la ragione sociale"); return;
    }
    setSaving(true);
    try {
      const payload = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      await api.put(`/persons/${person.id}`, payload);
      toast.success("Anagrafica aggiornata");
      setOpen(false);
      onDone && onDone();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  const sectionCls = "rounded-xl border bg-card px-4";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-auto ras-scroll" data-testid="anagrafica-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Aggiorna anagrafica</DialogTitle>
        </DialogHeader>

        <Accordion type="multiple" defaultValue={["anagrafica", "categorie"]} className="space-y-3">
          {/* Dati anagrafici */}
          <AccordionItem value="anagrafica" className={sectionCls} data-testid="anagrafica-section-dati">
            <AccordionTrigger className="hover:no-underline">
              <span className="flex items-center gap-2 font-medium text-sm"><User className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Dati anagrafici</span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Cognome</Label><Input value={f.cognome} onChange={(e) => set("cognome", e.target.value)} className="mt-1" data-testid="anag-cognome" /></div>
                <div><Label>Nome</Label><Input value={f.nome} onChange={(e) => set("nome", e.target.value)} className="mt-1" data-testid="anag-nome" /></div>
                <div><Label>Ragione sociale (persona giuridica)</Label><Input value={f.ragione_sociale} onChange={(e) => set("ragione_sociale", e.target.value)} className="mt-1" data-testid="anag-ragione-sociale" /></div>
                <div><Label>Ente committente</Label><Input value={f.ente_committente} onChange={(e) => set("ente_committente", e.target.value)} className="mt-1" data-testid="anag-ente-committente" /></div>
                <div><Label>Codice fiscale</Label><Input value={f.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} maxLength={16} className="mt-1" data-testid="anag-cf" /></div>
                <div><Label>Partita IVA</Label><Input value={f.partita_iva} onChange={(e) => set("partita_iva", e.target.value)} className="mt-1" data-testid="anag-piva" /></div>
                <div><Label>N. tessera associativa</Label><Input value={f.numero_tessera} onChange={(e) => set("numero_tessera", e.target.value)} className="mt-1" data-testid="anag-tessera" /></div>
                <div><Label>Codice ente affiliazione</Label><Input value={f.codice_ente_affiliazione} onChange={(e) => set("codice_ente_affiliazione", e.target.value)} className="mt-1" data-testid="anag-codice-ente" /></div>
                <div><Label>Data di nascita</Label><Input type="date" max={todayISO()} value={f.data_nascita} onChange={(e) => set("data_nascita", e.target.value)} className="mt-1" data-testid="anag-data-nascita" /></div>
                <div><Label>Età (calcolata)</Label><Input value={eta === "" ? "" : `${eta} anni`} readOnly disabled className="mt-1 bg-muted" data-testid="anag-eta" /></div>
                <div><Label>Luogo di nascita</Label><Input value={f.luogo_nascita} onChange={(e) => set("luogo_nascita", e.target.value)} className="mt-1" data-testid="anag-luogo-nascita" /></div>
                <div><Label>Genere (facoltativo)</Label>
                  <Select value={f.genere || "nd"} onValueChange={(v) => set("genere", v === "nd" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="anag-genere"><SelectValue placeholder="Non specificato" /></SelectTrigger>
                    <SelectContent><SelectItem value="nd">Non specificato</SelectItem>{GENERI.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Indirizzo */}
          <AccordionItem value="indirizzo" className={sectionCls} data-testid="anagrafica-section-indirizzo">
            <AccordionTrigger className="hover:no-underline">
              <span className="flex items-center gap-2 font-medium text-sm"><MapPin className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Indirizzo</span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="grid sm:grid-cols-4 gap-3">
                <div className="sm:col-span-2"><Label>Indirizzo</Label><Input value={f.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} className="mt-1" data-testid="anag-indirizzo" /></div>
                <div><Label>N. civico</Label><Input value={f.numero_civico} onChange={(e) => set("numero_civico", e.target.value)} className="mt-1" data-testid="anag-civico" /></div>
                <div><Label>CAP</Label><Input value={f.cap} onChange={(e) => set("cap", e.target.value)} maxLength={5} className="mt-1" data-testid="anag-cap" /></div>
                <div className="sm:col-span-3"><Label>Città</Label><Input value={f.citta} onChange={(e) => set("citta", e.target.value)} className="mt-1" data-testid="anag-citta" /></div>
                <div><Label>Provincia</Label><Input value={f.provincia} onChange={(e) => set("provincia", e.target.value.toUpperCase())} maxLength={2} className="mt-1" data-testid="anag-provincia" /></div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Contatti e preferenze */}
          <AccordionItem value="contatti" className={sectionCls} data-testid="anagrafica-section-contatti">
            <AccordionTrigger className="hover:no-underline">
              <span className="flex items-center gap-2 font-medium text-sm"><Phone className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Contatti e preferenze</span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Telefono principale</Label><Input value={f.telefono_principale} onChange={(e) => set("telefono_principale", e.target.value)} className="mt-1" data-testid="anag-tel1" /></div>
                <div><Label>Telefono secondario</Label><Input value={f.telefono_secondario} onChange={(e) => set("telefono_secondario", e.target.value)} className="mt-1" data-testid="anag-tel2" /></div>
                <div><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="mt-1" data-testid="anag-whatsapp" /></div>
                <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="anag-email" /></div>
                <div><Label>PEC</Label><Input type="email" value={f.pec} onChange={(e) => set("pec", e.target.value)} className="mt-1" data-testid="anag-pec" /></div>
                <div><Label>Referente familiare</Label><Input value={f.referente_familiare} onChange={(e) => set("referente_familiare", e.target.value)} className="mt-1" data-testid="anag-referente" /></div>
                <div><Label>Modalità di contatto preferita</Label>
                  <Select value={f.modalita_contatto || "nd"} onValueChange={(v) => set("modalita_contatto", v === "nd" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="anag-modalita"><SelectValue placeholder="Scegli" /></SelectTrigger>
                    <SelectContent><SelectItem value="nd">Nessuna preferenza</SelectItem>{MODALITA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Fascia oraria preferita</Label>
                  <Select value={f.fascia_oraria || "nd"} onValueChange={(v) => set("fascia_oraria", v === "nd" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="anag-fascia"><SelectValue placeholder="Scegli" /></SelectTrigger>
                    <SelectContent><SelectItem value="nd">Nessuna preferenza</SelectItem>{FASCE.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Relazione e collegamenti */}
          <AccordionItem value="relazione" className={sectionCls} data-testid="anagrafica-section-relazione">
            <AccordionTrigger className="hover:no-underline">
              <span className="flex items-center gap-2 font-medium text-sm"><Link2 className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Relazione e collegamenti</span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Origine del contatto</Label><Input value={f.origine_contatto} onChange={(e) => set("origine_contatto", e.target.value)} placeholder="es. passaparola, sito web, altro servizio…" className="mt-1" data-testid="anag-origine" /></div>
                <div><Label>Stato della relazione</Label>
                  <Select value={f.stato_relazione || "Attivo"} onValueChange={(v) => set("stato_relazione", v)}>
                    <SelectTrigger className="mt-1" data-testid="anag-stato-relazione"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATI_RELAZIONE.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <Label>Enti collegati</Label>
                  <div className="mt-1 max-h-36 overflow-auto ras-scroll rounded-lg border p-2 space-y-1" data-testid="anag-enti">
                    {entities.length === 0 ? <p className="text-xs text-muted-foreground">Nessun ente disponibile</p> :
                      entities.map((en) => (
                        <label key={en.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={(f.enti_ids || []).includes(en.id)} onCheckedChange={() => toggleInArray("enti_ids", en.id)} />
                          <span className="truncate">{en.denominazione}</span>
                        </label>
                      ))}
                  </div>
                </div>
                <div>
                  <Label>Servizi collegati</Label>
                  <div className="mt-1 max-h-36 overflow-auto ras-scroll rounded-lg border p-2 space-y-1" data-testid="anag-servizi">
                    {services.length === 0 ? <p className="text-xs text-muted-foreground">Nessun servizio disponibile</p> :
                      services.map((sv) => (
                        <label key={sv.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={(f.servizi_ids || []).includes(sv.id)} onCheckedChange={() => toggleInArray("servizi_ids", sv.id)} />
                          <span className="truncate">{sv.nome}</span>
                        </label>
                      ))}
                  </div>
                </div>
              </div>
              <div className="mt-3"><Label>Tag (separati da virgola)</Label><Input value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="es. fragilità, minori, urgente" className="mt-1" data-testid="anag-tags" /></div>
              <div className="mt-3"><Label>Note generali</Label><DictationTextarea value={f.note} onValueChange={(v) => set("note", v)} rows={3} className="mt-1" placeholder="Scrivi o detta con il microfono…" data-testid="anag-note" /></div>
            </AccordionContent>
          </AccordionItem>

          {/* Categorie / tipo persona */}
          <AccordionItem value="categorie" className={sectionCls} data-testid="anagrafica-section-categorie">
            <AccordionTrigger className="hover:no-underline">
              <span className="flex items-center gap-2 font-medium text-sm"><Tags className="h-4 w-4 text-[color:var(--brand-orange-600)]" /> Categorie (tipo di persona)</span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <p className="text-xs text-muted-foreground mb-2">Una persona può appartenere a più categorie contemporaneamente.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {CLASSIFICATIONS.map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border px-2.5 py-2 hover:bg-muted" data-testid={`anag-classificazione-${c}`}>
                    <Checkbox checked={(f.classificazioni || []).includes(c)} onCheckedChange={() => toggleInArray("classificazioni", c)} />
                    <span className="capitalize">{c}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Metadati in sola lettura */}
          <AccordionItem value="meta" className={sectionCls} data-testid="anagrafica-section-meta">
            <AccordionTrigger className="hover:no-underline">
              <span className="flex items-center gap-2 font-medium text-sm"><Info className="h-4 w-4 text-muted-foreground" /> Informazioni scheda</span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <div><Label className="text-muted-foreground">Data creazione</Label><p className="mt-1" data-testid="anag-created-at">{fmtDateTime(person?.created_at)}</p></div>
                <div><Label className="text-muted-foreground">Operatore creatore</Label><p className="mt-1" data-testid="anag-creator">{creatorName}</p></div>
                <div><Label className="text-muted-foreground">Ultimo aggiornamento</Label><p className="mt-1" data-testid="anag-updated-at">{fmtDateTime(person?.updated_at)}</p></div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="anag-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salva anagrafica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
